import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Editor from './item-content-editor';
import { Editable, useSlateStatic } from 'slate-react';
import styles from '../../style/item-content/item-content.module.css';
import AspectRatioWrapper from '../aspect-ratio-wrapper';
import FontSizeWrapper from '../font-size-wrapper';
import SizeWrapper from '../size-wrapper';
import { evaluate, isNull } from '../expression';
import { DatePicker, notification } from 'antd';
import { useCurrentItem, useData, useDataActions, useTimestamps } from './polling-context';
import { useReactToPrint } from 'react-to-print';
import { Transforms } from 'slate';
import moment from 'moment';
import { getFontFamilyStyle } from '../../utils/font-family';

// TODO: either disable drag and drop and data transfer of files, or properly implement it.

/*
    ItemContent
    props:
    {
        passive: Bool // true || undefined. function element will not modify data. media muted by default.
        printDocumentTitle: String // optional. A name to call the printed document. defaults to 'Schoolhouse'.
        printRef: Ref // optional. If a ref is provided, it's current will be set to the function that triggers printing the item content.
    }
    - note: varkey attributes from data override (not replace) element properties if present; so element will have default property after unsetting data override.
*/
// Row count above which a table is treated as too tall to keep on one printed
// page. A report page holds roughly 30-40 body lines at report font size, so a
// table past a dozen rows is a standalone full-width table that can plausibly
// overflow; below that, `break-inside: avoid` is satisfiable and preferable.
export const LONG_TABLE_ROW_COUNT = 12;

// Injected verbatim into the print iframe's <head> by react-to-print, so
// unlike the inline styles on the elements below these rules CAN be
// media-queried — which is the whole reason the fix lives here rather than in
// TableElement's style prop: on-screen rendering must not change (PIN-205 #4).
//
// Every override is !important because inline styles otherwise win outright,
// and because copyStyles appends the app's own sheets AFTER this block.
//
// Long tables are de-monolithised so Chrome fragments them between rows
// instead of slicing an atomic inline at the page boundary. Short tables
// match none of these rules and keep inline-flex + break-inside:avoid
// exactly as before.
//
// A long table WITHOUT a rowSpan cell becomes a real CSS table box. That is
// the only way to express a repeating header in CSS: `table-header-group`
// makes Chrome reprint the header on every page the table spans, so a
// continuation page is not a wall of unlabeled rows. `inline-table` would
// not serve — it is an atomic inline, i.e. exactly the unfragmentable box
// being removed here. Cells already carry a `width` beside their `flex`
// (see TableCellElement), since `display: table-cell` ignores `flex`;
// table-layout:fixed then makes those percentages authoritative rather
// than the hints `auto` would treat them as.
//
// The table, its rows and its cells switch together. Giving the header real
// cells while the body rows stayed flex established real columns the flex
// rows could not participate in: their cells collapsed to 1px and the table
// grew to 29,000px, repeating the header over 128 near-empty pages.
//
// A long table WITH a rowSpan cell keeps the older display:block path. Its
// overlay cell is absolutely positioned, so it contributes nothing to CSS
// table column sizing: the gap its row reserves would collapse and the
// overlay would land on top of real content.
//
// The last rule is the block that WRAPS a long table. Tables are Slate
// inlines, so one always sits inside a DefaultElement div, and that div
// carries break-inside:avoid like every other block. Relaxing the table
// alone left the wrapper unfragmentable, so a 60-row table still moved to
// the next page whole — printing its heading above a blank page (PIN-208 #3).
//
// Slate pads an inline table with empty text nodes on both sides. Beside an
// inline-flex table they share its line, but once the table is a block-level
// CSS table each becomes a blank line of its own — a gap between the table
// and its heading (PIN-319). Only empty (zero-width) text nodes are hidden.
export const PRINT_PAGE_STYLE = `@page { margin: 10%; }
@media print {
  [data-report-table][data-report-long-table='true'][data-report-has-row-span='false'] {
    display: table !important;
    table-layout: fixed !important;
    break-inside: auto !important;
  }
  [data-report-table][data-report-long-table='true'][data-report-has-row-span='false']
    > [data-report-table-row] {
    display: table-row !important;
  }
  [data-report-table][data-report-long-table='true'][data-report-has-row-span='false']
    [data-report-table-row] > [data-report-table-cell] {
    display: table-cell !important;
  }
  [data-report-table][data-report-long-table='true'][data-report-has-row-span='false']
    > [data-report-header-row='true'] {
    display: table-header-group !important;
  }
  [data-report-table][data-report-long-table='true'][data-report-has-row-span='true'] {
    display: block !important;
    break-inside: auto !important;
  }
  [data-report-table-row] {
    break-inside: avoid !important;
  }
  [data-report-table-cell] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  [data-report-block-has-long-table='true'] {
    break-inside: auto !important;
  }
  [data-report-block-has-long-table='true'] > [data-slate-node='text']:has([data-slate-zero-width]) {
    display: none !important;
  }
}`;

const ItemContent = (props) => {
  const editor = useSlateStatic();
  const currentItem = useCurrentItem();

  const containerRef = useRef();

  const passive = useMemo(() => {
    return props.passive;
  }, [props.passive]);

  const [editableStyleOverride, setEditableStyleOverride] = useState({});
  const [containerStyleOverride, setContainerStyleOverride] = useState({});
  const [shouldPrint, setShouldPrint] = useState(false);
  const [renderCount, setRenderCount] = useState(0); // a countdown of extra renders before printing

  const triggerPrint = useCallback(() => {
    // width/height used to be a hardcoded 210mm/118.2mm (A4-portrait-width,
    // 16:9-derived height). That fixed physical width is wider than the
    // printable area left by `@page { margin: 10%; }` and doesn't respond to
    // an orientation picked in the print dialog, cutting off the right edge
    // in both portrait and landscape (PIN-183 #5). Filling the printable
    // area instead lets it size to whatever page/orientation the browser
    // applies.
    setContainerStyleOverride({ height: '100%', width: '100%' });
    // `MozDisplay` was a typo for `display`: it resolves to the nonexistent
    // `-moz-display` and never lands on the element, so the intended block
    // never applied and the print root kept .content-body's
    // `display: inline-block` — an atomic inline, which
    // is monolithic and therefore unfragmentable, sliced at the page boundary
    // rather than flowing across pages. `height: auto` likewise undoes
    // .content-body's `height: 100%`, which resolves against the print
    // iframe's body and clamps the flow to a single page (PIN-205 #4).
    setEditableStyleOverride({ display: 'block', height: 'auto', overflow: 'visible' });
    setShouldPrint(true);
    setRenderCount(10);
  }, []);

  const print = useReactToPrint({
    content: () => containerRef.current,
    onAfterPrint: () => {
      setContainerStyleOverride({});
      setEditableStyleOverride({});
      setShouldPrint(false);
    },
    // break-inside avoidance on rows/blocks (see TableRowElement,
    // DefaultElement) keeps the Score Summary table and item-response lines
    // from splitting across a page boundary (PIN-183 #4), and the @media
    // print rules let a table too tall for one page break between its rows
    // instead of being sliced through one (PIN-205 #4).
    pageStyle: PRINT_PAGE_STYLE,
    documentTitle: props.printDocumentTitle,
    removeAfterPrint: true
  });

  const renderElement = useCallback(
    (props) => {
      switch (props.element.type) {
        case 'audio':
          return <AudioElement {...props} passive={passive} />;
        case 'code':
          return <CodeElement {...props} />;
        case 'fade':
          return <FadeElement {...props} />;
        case 'function':
          return <FunctionElement {...props} passive={passive} />;
        case 'hide':
          return <HideElement {...props} />;
        case 'input':
          return <InputElement {...props} />;
        case 'dateInput':
          return <DateInputElement {...props} />;
        case 'image':
          return <ImageElement {...props} />;
        case 'outlineCycle':
          return <OutlineCycleElement {...props} />;
        case 'table':
          return <TableElement {...props} />;
        case 'tableRow':
          return <TableRowElement {...props} />;
        case 'tableCell':
          return <TableCellElement {...props} />;
        case 'video':
          return <VideoElement {...props} passive={passive} />;
        default:
          return <DefaultElement {...props} />;
      }
    },
    [passive]
  );

  // scroll content to top when item changes
  useEffect(() => {
    Editor.scrollToTop(editor);
  }, [currentItem, editor]);

  // assign triggerPrint to ref passed down by parent
  useEffect(() => {
    if (props.printRef) props.printRef.current = triggerPrint;
  }, [triggerPrint, props.printRef]);

  // burn a few extra renders before printing to allow dynamicly sized components to do their thing
  useEffect(() => {
    if (!shouldPrint) return;
    if (renderCount === 0) print();
    else setRenderCount(renderCount - 1);
  }, [print, renderCount, shouldPrint]);

  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  const editableElement = (
    <Editable
      style={editableStyleOverride}
      className={styles['content-body']}
      canselect={editor.readOnly ? 'false' : 'true'}
      readOnly={editor.readOnly}
      renderElement={renderElement}
      renderLeaf={renderLeaf}
    />
  );

  return (
    <div className={styles['container']} style={containerStyleOverride}>
      {props.layout === 'report' ? (
        <div
          className={styles['report-container']}
          data-testid="report-container"
          ref={containerRef}
        >
          {editableElement}
        </div>
      ) : (
        <SizeWrapper>
          <AspectRatioWrapper aspectRatio={16 / 9}>
            <FontSizeWrapper
              fontSizeForDimensions={(height, width) => Math.min(height, width) * 0.013}
              ref={containerRef}
            >
              {editableElement}
            </FontSizeWrapper>
          </AspectRatioWrapper>
        </SizeWrapper>
      )}
    </div>
  );
};

ItemContent.defaultProps = {
  layout: 'slide'
};

export default ItemContent;

const AudioElement = ({ attributes, children, element, passive }) => {
  return (
    <span {...attributes} contentEditable={false} draggable={false}>
      <audio
        // autoPlay
        controls
        controlsList="nodownload"
        muted={!!passive}
        style={{ width: `${element.width}%` }}
      >
        <source src={element.url} />
      </audio>
      {children}
    </span>
  );
};

const CodeElement = ({ attributes, children, element }) => {
  return (
    <pre {...attributes} style={{ textAlign: element.textAlign }}>
      <code>{children}</code>
    </pre>
  );
};

// A heading for "keep with what follows" purposes. Font size cannot identify
// one across templates — WCAT's section headings are bold at 18, the same
// size STARS uses for body text — but being entirely bold can: heading
// paragraphs are wholly bold, body copy and label/value lines are not
// (PIN-208 #3).
const isHeadingBlock = (element) => {
  const leaves = (element.children || []).filter((leaf) => (leaf.text || '').trim());
  return leaves.length > 0 && leaves.every((leaf) => leaf.bold);
};

// A table too tall to fit one page, which print may split between its rows.
// Counted from the children rather than read off table.rowCount: the
// normalizer sets rowCount on the tableRow nodes, never on the table
// (item-content-plugin.js). A template marks a table `keepTogether` when it
// fits one page and must move to the next page whole rather than split
// (PIN-319).
const isLongTable = (table) =>
  !table.keepTogether &&
  (table.children || []).filter((c) => c.type === 'tableRow').length > LONG_TABLE_ROW_COUNT;

// A block holding a table too tall to fit one page. That table is switched to
// a fragmentable box by PRINT_PAGE_STYLE; this block has to be able to
// fragment too, or the table is moved whole to the next page (PIN-208 #3).
const hasLongTable = (element) =>
  (element.children || []).some((child) => child.type === 'table' && isLongTable(child));

export const DefaultElement = ({ attributes, children, element }) => {
  return (
    <div
      {...attributes}
      // Hook for the @media print rule in PRINT_PAGE_STYLE — see hasLongTable.
      data-report-block-has-long-table={hasLongTable(element) ? 'true' : 'false'}
      style={{
        lineHeight: element.lineHeight || 1.2, // TODO: 1.2 is a temp default. remove when lineHeight is implemented.
        marginBottom: '0.5vmin',
        textAlign: element.textAlign,
        // Keeps a single block (e.g. an item-response line) from splitting
        // across a printed page boundary (PIN-183 #4).
        breakInside: 'avoid',
        // Page break before a section the report template explicitly asked
        // to start on its own page (PIN-183 #4). PIN-184 also inferred one
        // from a heading-sized bold run, which fired on every section
        // heading and printed a mostly-blank page for each; `break-after:
        // avoid` below keeps headings with their content instead, so the
        // inference is no longer needed.
        ...(element.pageBreakBefore ? { breakBefore: 'page' } : {}),
        // Keeps a heading on the same page as the table or text it
        // introduces, instead of stranding it at a page boundary (PIN-208 #3).
        ...(isHeadingBlock(element) ? { breakAfter: 'avoid' } : {})
      }}
    >
      {children}
    </div>
  );
};

const FadeElement = ({ attributes, children, element }) => {
  const editor = useSlateStatic();

  return (
    <span
      {...attributes}
      onMouseDown={(event) => {
        if (!editor.interactionEnabled) return;
        const nodePath = Editor.findPath(editor, element);
        Editor.setNodeProperties(editor, element.type, { didStart: true }, nodePath);
      }}
      style={
        editor.readOnly && (!element.manualStart || element.didStart)
          ? {
              animation: element.name,
              animationDelay: `${element.delay}s`,
              animationDuration: `${element.duration}s`,
              animationFillMode: element.fillMode,
              opacity: element.opacity
            }
          : null
      }
    >
      {children}
    </span>
  );
};

const FunctionElement = ({ attributes, children, element, passive }) => {
  const editor = useSlateStatic();
  const data = useData(element.dataKeyDependencies);
  const { currentItemTimestamp } = useTimestamps();

  useEffect(() => {
    if (
      editor.readOnly &&
      !isNull(element.initExpression) &&
      currentItemTimestamp === editor.currentItemTimestamp
    ) {
      // evaluate then unset initExpression
      Editor.withoutNormalizing(editor, () => {
        const path = Editor.findPath(editor, element);
        try {
          evaluate({
            node: element.initExpression,
            editor: editor,
            path,
            passive
          });
        } catch (error) {
          notification['warning']({
            message: 'Expression failed to evaluate:',
            description: error.message
          });
        }

        // unset the init expression so it doesn't run again
        Transforms.unsetNodes(editor, 'initExpression', { at: path });
      });
    }
  }, [currentItemTimestamp, data, editor, element, passive]);

  // eval update expression on data or node change
  useEffect(() => {
    // don't eval update expressions until init has been eval'd
    if (!isNull(element.initExpression)) return;

    // evaluate update expression
    if (
      editor.readOnly &&
      element.updateExpression &&
      !isNull(element.updateExpression) &&
      currentItemTimestamp === editor.currentItemTimestamp
    ) {
      const path = Editor.findPath(editor, element);
      try {
        evaluate({
          node: element.updateExpression,
          editor: editor,
          path,
          passive
        });
      } catch (error) {
        notification['warning']({
          message: 'Expression failed to evaluate:',
          description: error.message
        });
      }
    }
  }, [currentItemTimestamp, data, editor, element, passive]);

  return (
    <span
      {...attributes}
      onMouseDown={(event) => {
        if (
          editor.readOnly &&
          editor.interactionEnabled &&
          element.expression &&
          !isNull(element.expression) &&
          currentItemTimestamp === editor.currentItemTimestamp
        ) {
          try {
            evaluate({
              node: element.expression,
              editor: editor,
              path: Editor.findPath(editor, element),
              passive
            });
          } catch (error) {
            notification['warning']({
              message: 'Expression failed to evaluate:',
              description: error.message
            });
          }
        }
      }}
      style={
        !editor.readOnly
          ? {
              border: '2px solid green',
              borderRadius: '5px'
            }
          : {}
      }
    >
      {children}
    </span>
  );
};

const HideElement = ({ attributes, children, element }) => {
  const data = useData();
  const editor = useSlateStatic();

  return (
    <div
      {...attributes}
      style={
        editor.readOnly
          ? {
              display: data[element.varKey] === true ? 'none' : ''
            }
          : {
              border: '2px solid blue',
              borderRadius: '5px'
            }
      }
    >
      {children}
    </div>
  );
};

/*
attributes:
_opacity number 0.0 - 1.0
_width number % of slate width
*/
const ImageElement = ({ attributes, children, element }) => {
  const data = useData();

  // opacity
  let opacity = '';
  if (typeof data[`${element.varKey}_opacity`] === 'number')
    opacity = data[`${element.varKey}_opacity`];
  else if (typeof element.opacity === 'number') opacity = element.opacity;

  return (
    <span {...attributes} contentEditable={false} draggable={false}>
      <img
        alt=""
        className={styles['image']}
        draggable={false}
        src={element.url}
        style={{
          opacity: opacity,
          width: `${data[`${element.varKey}_width`] || element.width}%`
        }}
      />
      {children}
    </span>
  );
};

// TODO: override addMark and removeMark to decend into voids, instead of rewritting leaf mark logic
const InputElement = ({ attributes, children, element }) => {
  const data = useData();
  const editor = useSlateStatic();
  const { updateData } = useDataActions();

  const style = useMemo(() => {
    return {
      backgroundColor: '#f0f5f5',
      border: '2px dashed #669999',
      borderRadius: '0.2vmin'
    };
  }, []);

  // render as a regular text leaf node while editing
  if (!editor.readOnly) {
    return (
      <span {...attributes} style={style}>
        {children}
      </span>
    );
  }

  // render the void input element while not editing
  return (
    <input
      {...attributes}
      onChange={(event) => updateData({ [element.key]: event.target.value })}
      style={{
        ...style,
        backgroundColor: element.children[0].backgroundColor || '',
        color: element.children[0].color || '',
        fontFamily: getFontFamilyStyle(element.children[0].fontFamily),
        fontSize: element.children[0].fontSize ? `${element.children[0].fontSize * 10}%` : '',
        fontStyle: element.children[0].italic ? 'italic' : 'normal',
        fontWeight: element.children[0].bold ? 'bold' : 'normal',
        height: '1.2em', // TOOD: this will need to be calculated dynamically 'calc(1em * ancestorParagraphLineHeight)' when line height is implemented
        width: 'calc(1em * 10)' // TODO: this will need to be dynamic
      }}
      type="text"
      value={data[element.key] || ''}
    >
      {children}
    </input>
  );
};

const DateInputElement = ({ attributes, children, element }) => {
  const data = useData();
  const editor = useSlateStatic();
  const { updateData } = useDataActions();

  const style = useMemo(() => {
    return {
      backgroundColor: '#f0f5f5',
      border: '2px dashed #669999',
      borderRadius: '0.2vmin'
    };
  }, []);

  // render as a regular text leaf node while editing
  if (!editor.readOnly) {
    return (
      <span {...attributes} style={style}>
        {children}
      </span>
    );
  }

  // render the void input element while not editing
  return (
    <span {...attributes} contentEditable={false} draggable={false}>
      <DatePicker
        format="MM/DD/YYYY"
        onChange={(date, dateString) => updateData({ [element.varKey]: dateString })}
        style={{
          fontSize: '1.75vmin',
          width: '16vmin',
          height: '3vmin'
        }}
        value={data[element.varKey] ? moment(data[element.varKey], 'MM/DD/YYYY') : null}
      />
    </span>
  );
};

/*
attributes:
_backgroundColor string
_color string
_fontFamily string
_fontSize number
_fontSyle string
_fontWeight string
_opacity number 0.0 - 1.0
_strikethrough truthy
_underline truthy
*/
export const Leaf = ({ attributes, children, leaf }) => {
  // properties from data override (not replace) leaf properties if present; so leaf will have default property after unsetting data override.
  const data = useData(leaf.dataKeyDependencies);

  // opacity
  let opacity = '';
  if (typeof data[`${leaf.varKey}_opacity`] === 'number') opacity = data[`${leaf.varKey}_opacity`];
  else if (typeof leaf.opacity === 'number') opacity = leaf.opacity;

  // strikethrough / underline
  let textDecoration = '';
  if (data[`${leaf.varKey}_strikethrough`] !== undefined) {
    if (data[`${leaf.varKey}_strikethrough`]) {
      textDecoration += 'line-through ';
    }
  } else if (leaf.strikethrough) {
    textDecoration += 'line-through ';
  }
  if (data[`${leaf.varKey}_underline`] !== undefined) {
    if (data[`${leaf.varKey}_underline`]) {
      textDecoration += 'underline ';
    }
  } else if (leaf.underline) {
    textDecoration += 'underline ';
  }

  const fontFamily = getFontFamilyStyle(data[`${leaf.varKey}_fontFamily`] || leaf.fontFamily);

  return (
    <span
      {...attributes}
      style={{
        backgroundColor: data[`${leaf.varKey}_backgroundColor`] || leaf.backgroundColor || '',
        color: data[`${leaf.varKey}_color`] || leaf.color || '',
        display: data[`${leaf.varKey}_display`] || leaf.display || '',
        fontFamily,
        fontSize: data[`${leaf.varKey}_fontSize`]
          ? `${data[`${leaf.varKey}_fontSize`] * 10}%`
          : leaf.fontSize
            ? `${leaf.fontSize * 10}%`
            : '',
        fontStyle:
          data[`${leaf.varKey}_italic`] !== undefined
            ? data[`${leaf.varKey}_italic`]
              ? 'italic'
              : 'normal'
            : leaf.italic
              ? 'italic'
              : 'normal',
        lineHeight: data[`${leaf.varKey}_lineHeight`] || leaf.lineHeight || 'normal',
        opacity: opacity,
        fontWeight:
          data[`${leaf.varKey}_bold`] !== undefined
            ? data[`${leaf.varKey}_bold`]
              ? 'bold'
              : 'normal'
            : leaf.bold
              ? 'bold'
              : 'normal',
        textDecoration: textDecoration,
        whiteSpace: typeof leaf.text === 'string' && leaf.text.includes('\n') ? 'pre-wrap' : ''
      }}
    >
      {children}
    </span>
  );
};

// TODO: remove, replace functionality with data attributes
const OutlineCycleElement = ({ attributes, children, element }) => {
  const editor = useSlateStatic();

  return (
    <span
      {...attributes}
      onClick={(event) => {
        if (!editor.interactionEnabled) return;
        const nodePath = Editor.findPath(editor, element);
        const nextOutlineIndex = (element.currentOutlineIndex + 1) % element.outlines.length;
        Editor.setNodeProperties(
          editor,
          element.type,
          { currentOutlineIndex: nextOutlineIndex },
          nodePath
        );
      }}
      style={{ outline: element.outlines[element.currentOutlineIndex] }}
    >
      {children}
    </span>
  );
};

// The header row is the table's first `tableRow` — the schema has no header
// concept (header cells differ only by shading or bold, and some data rows
// are bold too), so position is the only sound signal. TableElement publishes
// that node here and each row compares itself against it; Slate node
// identity is stable across renders unless the document is edited.
const TableHeaderRowContext = React.createContext(null);

const firstRow = (element) =>
  (element.children || []).find((child) => child.type === 'tableRow') || null;

const hasRowSpanCell = (element) =>
  (element.children || []).some((row) => (row.children || []).some((cell) => cell.rowSpan > 1));

export const TableElement = ({ attributes, children, element }) => {
  return (
    <span
      {...attributes}
      // Hooks for the @media print rules in PRINT_PAGE_STYLE. A table too
      // tall for one page is switched to a block box there so it fragments
      // between its rows instead of being sliced through one (PIN-205 #4).
      data-report-table="true"
      data-report-long-table={isLongTable(element) ? 'true' : 'false'}
      // A rowSpan overlay is out of flow, so it contributes nothing to CSS
      // table column sizing — the gap its row reserves would collapse and
      // the overlay would cover real content. Such tables keep the block
      // fragmentation path instead (see PRINT_PAGE_STYLE).
      data-report-has-row-span={hasRowSpanCell(element) ? 'true' : 'false'}
      style={{
        alignItems: 'stretch',
        // The table box closes the grid's right and bottom edges only. Its
        // top and left are drawn per-cell instead, because a fragmented box
        // paints its block-start border on the first fragment alone
        // (box-decoration-break: slice) — which left a long table's
        // continuation page with no top border.
        borderRight: `${element.border || ''}`,
        borderBottom: `${element.border || ''}`,
        // Keeps the whole table together on one printed page, not just
        // individual rows (see TableRowElement) — PIN-184. Kept for short
        // tables, which can honor it; the print stylesheet relaxes it to
        // `auto` for long ones, where it is unsatisfiable anyway.
        breakInside: 'avoid',
        display: 'inline-flex',
        flexDirection: 'column',
        verticalAlign: 'middle',
        width: `${element.width}%`
      }}
    >
      <TableHeaderRowContext.Provider value={firstRow(element)}>
        {children}
      </TableHeaderRowContext.Provider>
    </span>
  );
};

export const TableRowElement = ({ attributes, children, element }) => {
  // A rowSpan cell is position:absolute against this row (see
  // TableCellElement). Absolutely positioned boxes do not fragment, so a page
  // break landing between the two rows of a spanned pair would clip the
  // overlay. Harmless while no table fragmented at all; now that long ones do,
  // that break point has to be forbidden (PIN-205 #4).
  const hasRowSpan = (element.children || []).some((c) => c.rowSpan > 1);
  const headerRow = useContext(TableHeaderRowContext);

  return (
    <span
      {...attributes}
      // Hook for the @media print rule that keeps a row whole once its table
      // is allowed to break between rows (PIN-205 #4).
      data-report-table-row="true"
      // Hook for the @media print rule that repeats this row on every page
      // its table spans (see TableHeaderRowContext).
      data-report-header-row={headerRow && element === headerRow ? 'true' : 'false'}
      style={{
        display: 'flex',
        flex: `0 0 ${100 / element.rowCount}%`,
        flexDirection: 'row',
        width: '100%',
        // Keeps a Score Summary row from splitting across a printed page
        // boundary (PIN-183 #4).
        breakInside: 'avoid',
        ...(hasRowSpan ? { breakAfter: 'avoid' } : {}),
        // Positioning context for a rowSpan cell in this row (see
        // TableCellElement) to anchor its absolute overlay against.
        position: 'relative'
      }}
    >
      {children}
    </span>
  );
};

export const TableCellElement = ({ attributes, children, element }) => {
  const baseStyle = {
    // Report templates have emitted `backgroundColor` on cells since PIN-187
    // (header cells at #e8e8e8), but nothing ever read it, so every table
    // rendered flat white. Honoring it paints those headers and the STARS
    // Consistency table's alternating pair shading (PIN-199).
    backgroundColor: (!element.spacer && element.backgroundColor) || '',
    // Each cell draws its own top and left edge, so whichever row lands
    // first on a printed continuation page carries a top border by
    // construction (see TableElement). Same grid as before — one
    // horizontal and one vertical border per cell.
    borderTop: element.spacer ? '' : `${element.border || ''}`,
    borderLeft: `${element.border || ''}`,
    // A spacer column separates two sections of a table (PIN-356): it keeps
    // the left edge that closes the cell before it, but leaves its top open
    // and covers the table's bottom edge beneath it.
    ...(element.spacer ? { boxShadow: '0 1px 0 0 #fff' } : {}),
    // Flex items default to min-width:auto, which refuses to shrink a cell
    // below its content's unwrapped width — forcing the row (and table)
    // wider than intended instead of wrapping text within the cell.
    minWidth: 0,
    overflow: 'clip',
    textAlign: element.textAlign || ''
  };

  const spanCellRef = useRef(null);

  // Measures and imperatively sets the actual combined height of the rows
  // this cell spans. A fixed `${rowSpan * 100}%` (i.e. this row's own
  // height) is only correct by coincidence when every spanned row happens
  // to render at the same height — item text of differing lengths wraps
  // to a different number of lines, so the two rows in a pair often don't
  // match, and the fixed-percentage cell visibly overshoots/undershoots
  // the pair's real combined height (PIN-184 follow-up).
  useLayoutEffect(() => {
    if (!(element.rowSpan > 1)) return undefined;
    const cell = spanCellRef.current;
    if (!cell) return undefined;

    // Slate interposes an invisible zero-width data-slate-node="text" span
    // between sibling block elements (for cursor bookkeeping) — plain
    // nextElementSibling chaining lands on that spacer instead of the next
    // row, undercounting every row after the first.
    const nextRow = (row) => {
      let sibling = row.nextElementSibling;
      while (sibling && sibling.getAttribute('data-slate-node') !== 'element') {
        sibling = sibling.nextElementSibling;
      }
      return sibling;
    };

    const spannedRows = () => {
      const rows = [];
      let row = cell.parentElement;
      for (let i = 0; i < element.rowSpan && row; i++) {
        rows.push(row);
        row = nextRow(row);
      }
      return rows;
    };

    const updateHeight = () => {
      const total = spannedRows().reduce((sum, row) => sum + row.getBoundingClientRect().height, 0);
      cell.style.height = `${total}px`;
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateHeight);
    spannedRows().forEach((row) => observer.observe(row));
    return () => observer.disconnect();
  }, [element.rowSpan]);

  // Hook for the @media print rule that turns cells into real table cells.
  // Targeting `> *` instead would sweep in Slate's zero-width spacer spans
  // and invent an extra column.
  const cellAttributes = { ...attributes, 'data-report-table-cell': 'true' };

  if (element.rowSpan > 1) {
    // The flex-based table has no native rowspan. This cell is pulled out of
    // normal flow and absolutely positioned to overlay the rows below it —
    // its own row's other cells still reserve this column's width (their
    // colCount already accounts for it), leaving the gap this cell fills.
    // Only supports the LAST column (STARS Consistency table's Discrepancy
    // column, PIN-183 follow-up); extend with a left offset if a future
    // table needs to span an earlier column.
    return (
      <span
        {...cellAttributes}
        ref={spanCellRef}
        style={{
          ...baseStyle,
          position: 'absolute',
          top: 0,
          right: 0,
          width: `${element.colWidth || 100 / element.colCount}%`
        }}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      {...cellAttributes}
      style={{
        ...baseStyle,
        flex: `0 0 ${element.colWidth || 100 / element.colCount}%`,
        // Same percentage as the flex basis, which wins while this is a
        // flex item — so this is inert on screen. It exists for print,
        // where a long table becomes a CSS table box and `display:
        // table-cell` ignores `flex` entirely (see PRINT_PAGE_STYLE).
        width: `${element.colWidth || 100 / element.colCount}%`
      }}
    >
      {children}
    </span>
  );
};

// TODO: need to add time to attributes to have the time consumables. it only affects first player that gets and clears the consummable attribute.
/*
attributes:
_controls bool // show/hide controls
_loop bool
_paused bool
_seek-absolute number consumed
_seek-relative number consumed
_stop bool true consumed
_volume number 0.0 - 1.0
_volume-relative number consumed
*/
export const VideoElement = ({ attributes, children, element, passive }) => {
  const data = useData();
  const { updateData } = useDataActions();
  const ref = useRef();

  const onPause = useCallback(() => {
    if (!element.varKey) return;
    updateData({ [`${element.varKey}_paused`]: true });
  }, [element, updateData]);

  const onPlay = useCallback(() => {
    if (!element.varKey) return;
    updateData({ [`${element.varKey}_paused`]: false });
  }, [element, updateData]);

  const onVolumeChange = useCallback(() => {
    if (!element.varKey) return;
    updateData({ [`${element.varKey}_volume`]: ref.current.volume });
  }, [element, updateData]);

  const play = useCallback(async () => {
    try {
      await ref.current.play();
    } catch (error) {
      console.log(error);
    }
  }, []);

  const pause = useCallback(() => {
    ref.current.pause();
  }, []);

  // init
  useEffect(() => {
    if (!element.varKey || data[`${element.varKey}_volume`] !== undefined) return;
    updateData({ [`${element.varKey}_volume`]: ref.current.volume });
  }, [data, element, updateData]);

  // event listeners
  useEffect(() => {
    const player = ref.current;

    player.addEventListener('pause', onPause);
    player.addEventListener('play', onPlay);
    player.addEventListener('volumechange', onVolumeChange);

    return () => {
      player.removeEventListener('pause', onPause);
      player.removeEventListener('play', onPlay);
      player.removeEventListener('volumechange', onVolumeChange);
    };
  }, [onPause, onPlay, onVolumeChange]);

  // apply data attributes
  useEffect(() => {
    if (!element.varKey) return;

    // play/pause
    if (data[`${element.varKey}_paused`] === false && ref.current.paused === true) play();
    else if (data[`${element.varKey}_paused`] === true && ref.current.paused === false) pause();

    // seek
    if (typeof data[`${element.varKey}_seek-absolute`] === 'number') {
      ref.current.currentTime = data[`${element.varKey}_seek-absolute`];
      updateData({ [`${element.varKey}_seek-absolute`]: null });
    } else if (typeof data[`${element.varKey}_seek-relative`] === 'number') {
      ref.current.currentTime = ref.current.currentTime + data[`${element.varKey}_seek-relative`];
      updateData({ [`${element.varKey}_seek-relative`]: null });
    }

    // stop
    if (data[`${element.varKey}_stop`] === true) {
      updateData({
        [`${element.varKey}_paused`]: true,
        [`${element.varKey}_stop`]: null
      });
      ref.current.currentTime = 0;
    }

    // volume
    if (
      typeof data[`${element.varKey}_volume`] === 'number' &&
      data[`${element.varKey}_volume`] !== ref.current.volume
    ) {
      let newVolume = data[`${element.varKey}_volume`];

      if (newVolume > 1 || newVolume < 0) {
        newVolume = Math.min(1, Math.max(0, newVolume)); // constrain to 0.0 - 1.0
        updateData({ [`${element.varKey}_volume`]: newVolume });
      } else {
        // apply volume to player
        ref.current.volume = newVolume;
      }
    } else if (typeof data[`${element.varKey}_volume-relative`] === 'number') {
      updateData({
        [`${element.varKey}_volume`]:
          ref.current.volume + data[`${element.varKey}_volume-relative`],
        [`${element.varKey}_volume-relative`]: null
      });
    }
  }, [data, element.varKey, play, pause, updateData]);

  return (
    <span {...attributes} contentEditable={false} draggable={false}>
      <video
        controls={data[`${element.varKey}_controls`] === false ? false : true}
        controlsList="nodownload"
        loop={data[`${element.varKey}_loop`] === true ? true : false}
        muted={!!passive}
        playsInline
        preload={passive ? 'none' : 'metadata'}
        ref={ref}
        style={{ width: `${element.width}%` }}
      >
        <source src={element.url} />
      </video>
      {children}
    </span>
  );
};
